import { useMemo } from 'react'

import { ContentFile } from '../../../services/api'
import { ContentAST } from '../../../transforms/ast'

export interface CachedChapterEntry {
  chapter: ContentFile
  ast: ContentAST
  /**
   * True while the cached content has edits Mongo doesn't have yet (unsaved at
   * switch-away, or a background save failed). Restored into hasUnsavedChanges
   * when the entry is reopened so the autosave loop picks the save back up.
   */
  dirty: boolean
}

/**
 * In-memory per-file content cache behind the editor tabs. Holds the last-seen
 * AST + file envelope for every file visited this session so switching back to
 * a tab is instant (no refetch, no blocking save). Safe because the app is
 * single-author / single-tab: the last state this client saw is authoritative.
 * A background revalidation on every cache hit (see useChapterNavigation)
 * covers the out-of-band writers (content-session finish, another device).
 */
export interface ChapterContentCache {
  get: (filename: string) => CachedChapterEntry | null
  set: (filename: string, chapter: ContentFile, ast: ContentAST, dirty: boolean) => void
  clearDirty: (filename: string) => void
  remove: (filename: string) => void
}

export function useChapterContentCache(enabled: boolean): ChapterContentCache | undefined {
  return useMemo(() => {
    if (!enabled) {
      return undefined
    }
    const entries = new Map<string, CachedChapterEntry>()
    return {
      get: (filename) => entries.get(filename) ?? null,
      set: (filename, chapter, ast, dirty) => {
        entries.set(filename, { chapter, ast, dirty })
      },
      clearDirty: (filename) => {
        const entry = entries.get(filename)
        if (entry) {
          entry.dirty = false
        }
      },
      remove: (filename) => {
        entries.delete(filename)
      },
    }
  }, [enabled])
}
